<template>
  <!-- Multiple calendars: title with dropdown -->
  <div v-if="hasMultipleCalendars" class="calendar-title">
    <SrpDropdown :placement="'bottom-start'" :offset="3">
      <template #clickableElement="{ isDropdownVisible }">
        <div class="calendar-title__trigger-row">
          <h1 class="global-h1 calendar-title__heading calendar-title__heading--clickable" :class="{ 'calendar-title__heading--open': isDropdownVisible }">
            <span class="calendar-title__text">
              <span class="calendar-title__name">{{ currentCalendarName }}</span>
              <span v-if="suffix" class="calendar-title__suffix">{{ suffix }}</span>
            </span>
            <IconEmbedded :name="isDropdownVisible ? 'caret-top_3' : 'caret-bottom_3'" :size="14" color="gray" class="calendar-title__caret" />
          </h1>
          <!--<LinkWithIcon class="calendar-title__change-link" isDottedUnderline color="gray">
            <template #icon><IconEmbedded name="reload_2" :size="14" color="gray" /></template>
            <span>change</span>
          </LinkWithIcon>-->
        </div>
      </template>

      <template #dropdown="{ close }">
        <div class="calendar-title__dropdown">
          <!--<div class="calendar-title__dropdown-header">Change Calendar</div>-->
          <div
            v-for="calendar in calendars"
            :key="calendar.calendarId"
            class="calendar-title__option"
            :class="{ 'calendar-title__option--selected': calendar.calendarId === selectedCalendarId }"
            @click="selectCalendar(calendar.calendarId, close)"
          >
            <span class="calendar-title__option-name">{{ getCalendarDisplayName(calendar) }}</span>
            <IconEmbedded v-if="calendar.calendarId === selectedCalendarId" name="check_3" :size="16" color="rgba(0, 128, 96, 1)" style="margin-left: 7px" />
          </div>
        </div>
      </template>
    </SrpDropdown>
  </div>

  <!-- Single calendar: plain title -->
  <div v-else-if="calendars.length === 1" class="calendar-title">
    <h1 class="global-h1 calendar-title__heading">
      <span class="calendar-title__text">
        <!--<span class="calendar-title__name">{{ currentCalendarName }}</span>-->
        <span class="calendar-title__name">Social</span>
        <span v-if="suffix" class="calendar-title__suffix">{{ suffix }}</span>
      </span>
    </h1>
  </div>

  <!-- Loading state: placeholder title -->
  <div v-else-if="isLoading" class="calendar-title">
    <h1 class="global-h1 calendar-title__heading calendar-title__heading--loading">
      <span class="calendar-title__name">Loading...</span>
    </h1>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import { getCalendarId, setCalendarId, isLiveCalendar, getShowTestCalendars, isTestCalendar } from "@logic/calendarManager";
import { CalendarInfo } from "@contracts/contentCalendar";

// Components
import SrpDropdown from "@components/ui/SrpDropdown.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

const props = withDefaults(
  defineProps<{
    customerId: string;
    suffix?: string; // Text to append after calendar name (e.g., "Config", "Posts")
  }>(),
  {
    suffix: "",
  }
);

const emit = defineEmits<{
  (e: "calendarChanged", calendarId: string): void;
}>();

// State
const calendars = ref<CalendarInfo[]>([]);
const isLoading = ref(true);
const selectedCalendarId = ref<string>("");

// Computed
const hasMultipleCalendars = computed(() => calendars.value.length > 1);

function getCalendarDisplayName(calendar: CalendarInfo): string {
  const name = calendar.calendarName ?? "Calendar";
  return isTestCalendar(calendar.calendarId) ? `TEST - ${name}` : name;
}

const currentCalendarName = computed(() => {
  const current = calendars.value.find(c => c.calendarId === selectedCalendarId.value);
  if (!current) return "Calendar";
  return getCalendarDisplayName(current);
});

// Methods
async function loadCalendars() {
  if (!props.customerId) return;

  isLoading.value = true;

  // Get the currently selected calendar ID for this customer
  const storedId = await getCalendarId(props.customerId);
  selectedCalendarId.value = storedId;

  // Fetch all available calendars for this customer
  const includeTest = getShowTestCalendars();
  calendars.value = await ContentCalendarRepo.getCalendarsForCustomer(props.customerId, includeTest);

  // Validate that the stored calendarId exists for this customer
  const calendarExists = calendars.value.some(c => c.calendarId === storedId);

  if (!calendarExists && calendars.value.length > 0) {
    // Fall back to the first live calendar
    const firstLive = calendars.value.find(c => isLiveCalendar(c.calendarId)) ?? calendars.value[0];
    selectedCalendarId.value = firstLive.calendarId;
    setCalendarId(props.customerId, firstLive.calendarId);
  }

  isLoading.value = false;
}

function selectCalendar(calendarId: string, closeDropdown: () => void) {
  if (calendarId === selectedCalendarId.value) {
    closeDropdown();
    return;
  }

  selectedCalendarId.value = calendarId;
  setCalendarId(props.customerId, calendarId);
  closeDropdown();

  emit("calendarChanged", calendarId);

  // Reload the page to refresh data for the new calendar
  window.location.reload();
}

// Lifecycle
onMounted(async () => {
  await loadCalendars();
});

// Watch for customerId changes
watch(
  () => props.customerId,
  () => {
    loadCalendars();
  }
);
</script>

<style scoped lang="scss">
.calendar-title {
  display: inline-flex;
  align-items: center;

  &__trigger-row {
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }

  &__change-link {
    font-size: 14px;
    cursor: pointer;
  }

  &__heading {
    display: inline-flex;
    align-items: center;
    margin: 0;
    user-select: none;

    &--clickable {
      cursor: pointer;
      padding: 6px 12px;
      border-radius: 8px;
      border: 1px solid rgba(0, 0, 0, 0.12);
      transition:
        border-color 0.15s ease,
        background-color 0.15s ease;

      &:hover {
        border-color: rgba(0, 128, 128, 0.3);
        background-color: rgba(0, 128, 128, 0.04);
      }
    }

    &--open {
      border-color: rgba(0, 128, 128, 0.4);
      background-color: rgba(0, 128, 128, 0.06);
    }

    &--loading {
      opacity: 0.5;
    }
  }

  // Wraps name + suffix so they flow together as inline text
  &__text {
    display: inline;
  }

  &__suffix {
    white-space: nowrap;
    margin-left: 0.3em;
  }

  &__caret {
    flex-shrink: 0;
    margin-left: 10px;
    padding-left: 10px;
    border-left: 1px solid rgba(0, 0, 0, 0.15);
    opacity: 1;
    color: inherit;
  }

  // Responsive: shrink the heading for smaller viewports
  @media (max-width: 768px) {
    &__heading {
      font-size: 22px;
      line-height: 26px;
    }
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    &__heading {
      font-size: 26px;
      line-height: 30px;
    }
  }

  &__dropdown {
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    border: 1px solid rgba(0, 0, 0, 0.08);
    overflow: hidden;
    min-width: 200px;
  }

  &__dropdown-header {
    padding: 10px 14px 8px;
    font-size: 12px;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  &__option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    cursor: pointer;
    transition: background-color 0.1s ease;

    &:hover {
      background-color: rgba(0, 128, 128, 0.06);
    }

    &--selected {
      background-color: rgba(0, 128, 128, 0.08);
    }

    &-name {
      font-size: 14px;
      color: #333;
    }
  }
}
</style>
